/**
 * AddToContactDialog — save an email sender as a permanent CRM contact.
 *
 * Prefilled from the sender (name split into first/last, email, a light company
 * guess from the domain) and editable so the record can be enriched with phone /
 * notes before saving. Creates a `vault_personal_contacts` entry (source='email')
 * — the same contacts that appear in the contact-book arche everywhere else.
 * Kept local to the email arche (no cross-arche imports).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { Loader2 } from 'lucide-react';
import { useCreateDetailedContact, nameParts } from '../hooks/useAddressBook';

interface AddToContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEmail: string;
  initialName?: string | null;
}

// Free/consumer mail domains never map to a company name.
const FREE_MAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'aol.com',
  'gmx.com',
  'zoho.com',
]);

/** Best-effort company guess from the sender's domain (editable, may be blank). */
function guessCompany(email: string): string {
  const domain = email.split('@')[1]?.trim().toLowerCase();
  if (!domain || FREE_MAIL_DOMAINS.has(domain)) return '';
  const label = domain.split('.')[0] ?? '';
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
}

export function AddToContactDialog({
  open,
  onOpenChange,
  initialEmail,
  initialName,
}: AddToContactDialogProps) {
  const navigate = useNavigate();
  const create = useCreateDetailedContact();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  // (Re)seed the form from the sender each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const parts = nameParts(initialName ?? '', initialEmail);
    setFirstName(parts.firstName);
    setLastName(parts.lastName ?? '');
    setEmail(initialEmail);
    setCompany(guessCompany(initialEmail));
    setPhone('');
    setNotes('');
  }, [open, initialEmail, initialName]);

  const canSave = email.trim().length > 0 && !create.isPending;

  const handleSave = () => {
    create.mutate(
      { firstName, lastName, email, company, phone, notes },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast({
            description: 'Added to Contacts',
            action: (
              <ToastAction
                altText="View in Contacts"
                onClick={() => navigate('/a/arche_contact_book')}
              >
                View
              </ToastAction>
            ),
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Add to Contacts</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="atc-first" className="text-xs">
                  First name
                </Label>
                <Input
                  id="atc-first"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="atc-last" className="text-xs">
                  Last name
                </Label>
                <Input
                  id="atc-last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="atc-email" className="text-xs">
                Email
              </Label>
              <Input
                id="atc-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="atc-company" className="text-xs">
                Company
              </Label>
              <Input
                id="atc-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="atc-phone" className="text-xs">
                Phone
              </Label>
              <Input id="atc-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="atc-notes" className="text-xs">
                Notes
              </Label>
              <Textarea
                id="atc-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={!canSave} onClick={handleSave}>
                {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add contact'}
              </Button>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
